﻿import { BaseEditor } from './base-editor';
import { IForm } from '../interface/form';
import { AsyncValidatorFn, ValidatorFn } from '../types/validator-fn';
import { IEditor } from '../interface/editor';
import { CalendarViewModel, TCalendarEditor } from './t-calendar-editor';
import { DatePickerMask } from '../utils/t-date-picker-mask';
import { localeInfo } from '../../locale';
import { convertToDate, convertToJsonDate, formatDate, tryConvertToDateByFormat } from '../../utils/date';
import { createPopper, Instance } from '@popperjs/core';
import { isContainsElement } from '../../utils/dom';

export const DatePickerEditorTagName = 't-date-picker';

export function createDatePickerEditor(form: IForm,
    name: string,
    element: HTMLElement,
    validators: ValidatorFn[],
    asyncValidators: AsyncValidatorFn[]): IEditor {
    return new TDatePickerEditor(form, name, element, validators, asyncValidators);
}

export class TDatePickerEditor extends BaseEditor<string> {
    calendarContiner: HTMLElement;
    calendarEditor: TCalendarEditor;
    inputConainerElement: HTMLElement;
    inputElement: HTMLInputElement;
    cleanButtonElement: HTMLButtonElement;
    toggleButtonElement: HTMLButtonElement;

    startDate = new Date();
    startView = CalendarViewModel.days;
    minDate?: Date;
    maxDate?: Date;
    placeholder?: string | null;

    isCalendarShown = false;
    popperInstance!: Instance;
    mask!: DatePickerMask;

    constructor(
        form: IForm,
        name: string,
        element: HTMLElement,
        validators: ValidatorFn[],
        asyncValidators: AsyncValidatorFn[]) {
        super(form, name, element, validators, asyncValidators);
        this.mask = new DatePickerMask(localeInfo.datePicker!.format!);

        const cc =  element.querySelector<HTMLElement>(`#${this.id}_calendar`);     
        const ice= element.querySelector<HTMLElement>(`#${this.id}_input_group`);
        const ie= element.querySelector<HTMLInputElement>(`#${this.id}_input`);
        const cbe = element.querySelector<HTMLButtonElement>(`#${this.id}_clean`);
        const tbe = element.querySelector<HTMLButtonElement>(`#${this.id}_toggle`);

        if(!cc || !ice || !ie || !cbe || !tbe) {
            throw new Error(`Invalid DOM of field ${name}`);
        }

        //elements
        this.calendarContiner =   cc;
        this.inputConainerElement = ice;
        this.inputElement = ie;
        this.cleanButtonElement = cbe;
        this.toggleButtonElement = tbe;
        this.popperInstance = createPopper(this.inputConainerElement, this.calendarContiner, {
            placement: 'bottom-start',
            modifiers: [
                { name: 'eventListeners', enabled: false }
            ]
        });


        //attributes
        //must set calendar attribute before init calendar instance
        const initPlaceholder = this.getAttributeValue('placeholder');
        if (initPlaceholder) {
            this.placeholder = initPlaceholder;
        } else {
            this.inputElement.value = this.mask.inputValue;
        }
        const initStartView = this.getAttributeValue('start-view');
        if (initStartView) {
            this.startView = Number(initStartView) as CalendarViewModel;
            this.calendarContiner.setAttribute('start-view', initStartView);
        }
        const initStartDate = this.getAttributeValue('start-date');
        if (initStartDate) {
            const initStartDateValue = convertToDate(initStartDate);
            if (initStartDateValue) {
                this.startDate = initStartDateValue;
                this.calendarContiner.setAttribute('start-date', initStartDate);
            }
        }
        const initMinDate = this.getAttributeValue('min-date');
        if (initMinDate) {
            const initMinDateValue = convertToDate(initMinDate);
            if (initMinDateValue) {
                this.minDate = initMinDateValue;
                this.calendarContiner.setAttribute('min-date', initMinDate);
            }
        }
        const initMaxDate = this.getAttributeValue('max-date');
        if (initMaxDate) {
            const initMaxDateValue = convertToDate(initMaxDate);
            if (initMaxDateValue) {
                this.maxDate = initMaxDateValue;
                this.calendarContiner.setAttribute('max-date', initMaxDate);
            }
        }

        //events
        this.inputElement.addEventListener('focus', (ev: Event) => {
            this.onInputFocus(ev);
        });
        this.inputElement.addEventListener('blur', (ev: Event) => {
            this.onInputBlur(ev);
        });
        this.inputElement.addEventListener('keydown', (ev: KeyboardEvent) => {
            this.onInputKeydown(ev);
        });
        this.inputElement.addEventListener('click', (ev: Event) => {
            this.onInputClick(ev);
        });
        this.cleanButtonElement.addEventListener('click', (ev: Event) => {
            this.clean();
        });
        this.toggleButtonElement.addEventListener('click', (ev: Event) => {
            this.switchCalendar();
        });
        document.addEventListener('mousedown', (ev: Event) => {
            this.documentClick(ev);
        });

        //init calendar instance
        this.calendarEditor = new TCalendarEditor(form, name, this.calendarContiner, validators, asyncValidators);
        this.calendarEditor.valueChanged = (value) => {
            this.onDateSelect(value);
        };

        
    }

    get value(): string {
        return this.calendarEditor.value as string;
    }
    set value(value: string) {
        const dateValue = convertToDate(value);
        if (dateValue) {
            this.calendarEditor.value = convertToJsonDate(dateValue)!;
            this.inputElement.value = formatDate(dateValue, localeInfo.datePicker!.format!);
        }
    }

    setDisabled(disabled: boolean): void {
        this.inputElement.disabled = disabled;
        this.setDisabledInternal(disabled);
    }


    switchCalendar():void {
        if (this.isCalendarShown) {
            this.hideCalendar();
        } else {
            this.showCalendar(true);
        }
    }

    showCalendar(forceFocus: boolean):void {
        this.isCalendarShown = true;
        //Enable the event listeners
        this.popperInstance.setOptions((options: any) => ({
            ...options,
            modifiers: [
                ...options.modifiers,
                { name: 'eventListeners', enabled: true },
            ],
        }));

        // Update its position
        this.popperInstance.update();

        this.calendarContiner.classList.remove('d-none');

        if (forceFocus) {
            this.inputElement.focus();
        }
    }

    hideCalendar():void {
        this.isCalendarShown = false;
        // Disable the event listeners
        this.popperInstance.setOptions((options: any) => ({
            ...options,
            modifiers: [
                ...options.modifiers,
                { name: 'eventListeners', enabled: false },
            ],
        }));
        this.calendarContiner.classList.add('d-none');
    }


    /**
     * on select the date in calendar
     * @param date 
     */
    onDateSelect(date: string | string[] | null):void {
        if (date == null || date.length===0) {
            this.clean();
        }
        else {
            let dateValue: Date | null = null;
            if (Array.isArray(date)) {
                //only support single selection
                dateValue = convertToDate(date[0]);
            }
            else {
                dateValue = convertToDate(date);
            }

            this.inputElement.value = formatDate(dateValue!, localeInfo.datePicker!.format!);
            this.mask.inputValue = this.inputElement.value;
            this.notifyChanges(this.formatValue(dateValue));
        }

        this.hideCalendar();
    }

    onInputFocus(ev: Event):void {
        this.showCalendar(false);
        if (this.inputElement.value === '') {
            this.inputElement.value = this.mask.inputValue;
        }

        this.mask.resetAllPostion();
        this.setInputBoxSelection();
    }

    onInputClick(ev: Event):void {
        const target = ev.target as HTMLInputElement;
        this.mask.setPosition(target.selectionStart!);
        this.setInputBoxSelection();
    }

    onInputBlur(ev: Event):void {
        const target = ev.target as HTMLInputElement;
        const inputValue = target.value;

        const m = tryConvertToDateByFormat(inputValue, localeInfo.datePicker!.format!);
        if (m != null) {
            const formatValue = this.formatValue(m);
            this.calendarEditor.value = formatValue;
            this.notifyChanges(formatValue);
        } else {
            //clean the invalid input
            this.clean();
        }
        this.hideCalendar();
    }

    onInputKeydown(ev: KeyboardEvent):void {
        this.mask.sendKey(ev.key);
        this.inputElement.value = this.mask.inputValue;
        this.setInputBoxSelection();
        const m = tryConvertToDateByFormat(this.inputElement.value, localeInfo.datePicker!.format!);
        if (m != null) {
            //once input a valid date, update the value
            const formatValue = this.formatValue(m);
            this.calendarEditor.value = formatValue;
            this.notifyChanges(formatValue);
        }
        if (ev.key !== 'Tab' && ev.key !== 'Enter') {
            ev.preventDefault();
        }
    }

    clean():void {
        this.mask.clean();
        if (!this.placeholder) {
            this.inputElement.value = this.mask.inputValue;
        } else {
            this.inputElement.value = '';
        }
        this.setInputBoxSelection();
        this.calendarEditor.value = null;
        this.notifyChanges(null);
    }


    private setInputBoxSelection() {
        setTimeout(() => {
            this.inputElement.setSelectionRange(this.mask.startPosition, this.mask.endPosition + 1);
            }, 50);
       
    }

    documentClick(ev: Event):void {
        const target = ev.target as HTMLElement;
            const isOutside = !isContainsElement([
                this.element,
                this.calendarContiner
            ], target);
            if (isOutside) {
                this.hideCalendar();
            } else if (isContainsElement(this.inputElement, target) === false) {
                //prevent default, we need keep focus on the inputbox
                ev.preventDefault();
            }
    }

    private formatValue(date: Date | null | undefined): string {
        if (date == null) {
            return '';
        }

        const v = convertToJsonDate(date);
        return v ?? '';

    }

    setFocus():void {
        this.inputElement.focus();
    }

}